import QRCode from 'qrcode'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase'

export const qrService = {
  // Generate QR code data URL
  async generateQRCode(data, options = {}) {
    const defaultOptions = {
      width: 200,
      margin: 2,
      errorCorrectionLevel: 'H',
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      ...options
    }

    try {
      // If a logo is requested, render to canvas and overlay it.
      if (defaultOptions.logoSrc && typeof document !== 'undefined') {
        const canvas = document.createElement('canvas')
        await QRCode.toCanvas(canvas, data, defaultOptions)

        const ctx = canvas.getContext('2d')
        if (!ctx) return canvas.toDataURL('image/png')

        const logoSize = Number(defaultOptions.logoSize || Math.round((defaultOptions.width || 200) * 0.22))
        const x = Math.round((canvas.width - logoSize) / 2)
        const y = Math.round((canvas.height - logoSize) / 2)

        // White rounded background for logo (better scan reliability)
        const pad = Math.round(logoSize * 0.18)
        const bgSize = logoSize + pad * 2
        const bx = Math.round((canvas.width - bgSize) / 2)
        const by = Math.round((canvas.height - bgSize) / 2)
        const r = Math.round(bgSize * 0.2)

        ctx.save()
        ctx.fillStyle = '#FFFFFF'
        ctx.beginPath()
        ctx.moveTo(bx + r, by)
        ctx.arcTo(bx + bgSize, by, bx + bgSize, by + bgSize, r)
        ctx.arcTo(bx + bgSize, by + bgSize, bx, by + bgSize, r)
        ctx.arcTo(bx, by + bgSize, bx, by, r)
        ctx.arcTo(bx, by, bx + bgSize, by, r)
        ctx.closePath()
        ctx.fill()
        ctx.restore()

        const img = new Image()
        img.crossOrigin = 'anonymous'
        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
          img.src = defaultOptions.logoSrc
        })

        ctx.drawImage(img, x, y, logoSize, logoSize)
        return canvas.toDataURL('image/png')
      }

      return await QRCode.toDataURL(data, defaultOptions)
    } catch (error) {
      console.error('Error generating QR code:', error)
      throw error
    }
  },

  // Generate unique session ID
  generateSessionId() {
    // Browser-compatible random ID generation
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  },

  // Generate customer QR code data (stable, per customer)
  generateCustomerQRData(customerId, customerPhone, customerCode = null) {
    return JSON.stringify({
      type: 'customer',
      id: customerId,
      code: customerCode || null,
      phone: customerPhone,
      version: '2.0',
    })
  },

  // Create QR session record in database
  async createQRSession(customerId, customerPhone, sessionId, qrData) {
    try {
      const sessionData = {
        customerId,
        customerPhone,
        sessionId,
        qrData,
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + (24 * 60 * 60 * 1000)), // 24 hours
        isUsed: false,
        usedAt: null,
        usedBy: null,
        branchId: null
      }

      const docRef = await addDoc(collection(db, 'qrSessions'), sessionData)
      return docRef.id
    } catch (error) {
      console.error('Error creating QR session:', error)
      throw error
    }
  },

  // Parse QR code data
  parseQRData(qrData) {
    try {
      return JSON.parse(qrData)
    } catch (error) {
      // If it's not JSON, treat as phone number (legacy support)
      return {
        type: 'phone',
        phone: qrData,
        version: '1.0'
      }
    }
  },

  // Generate customer QR code image (no storage upload)
  async generateCustomerQR(customerId, customerPhone, userId) {
    try {
      const qrData = this.generateCustomerQRData(customerId, customerPhone, null)
      const qrDataURL = await this.generateQRCode(qrData, {
        width: 300,
        logoSrc: '/brand/trendsetter-logo.png',
        logoSize: 72,
      })

      return {
        qrData,
        qrDataURL,
        downloadURL: null,
        sessionId: null,
      }
    } catch (error) {
      console.error('Error generating customer QR:', error)
      throw error
    }
  },

  // Validate QR code data with enhanced security
  validateQRData(qrData) {
    try {
      const parsed = this.parseQRData(qrData)
      
      // Check if QR code has expired
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        return { 
          isValid: false, 
          error: 'QR code has expired. Please generate a new one.' 
        }
      }

      // Check version compatibility
      if (parsed.version && parsed.version !== '2.0' && parsed.version !== '1.0') {
        return { 
          isValid: false, 
          error: 'QR code version not supported. Please update your app.' 
        }
      }
      
      if (parsed.type === 'customer') {
        return {
          isValid: true,
          type: 'customer',
          customerId: parsed.id,
          customerCode: parsed.code || null,
          phone: parsed.phone,
          sessionId: parsed.sessionId,
          timestamp: parsed.timestamp,
          expiresAt: parsed.expiresAt,
          version: parsed.version
        }
      } else if (parsed.type === 'phone') {
        return {
          isValid: true,
          type: 'phone',
          phone: parsed.phone,
          version: parsed.version || '1.0'
        }
      }
      
      return { isValid: false, error: 'Invalid QR code format' }
    } catch (error) {
      return { isValid: false, error: 'Failed to parse QR code' }
    }
  },

  // Mark QR session as used
  async markSessionAsUsed(sessionId, branchId, staffId) {
    try {
      // This would update the session record in the database
      // Implementation depends on your database structure
      console.log(`Session ${sessionId} marked as used at branch ${branchId} by staff ${staffId}`)
      return true
    } catch (error) {
      console.error('Error marking session as used:', error)
      throw error
    }
  },

  // Generate one-time QR code for immediate use
  async generateOneTimeQR(customerId, customerPhone) {
    try {
      const sessionId = this.generateSessionId()
      const qrData = this.generateCustomerQRData(customerId, customerPhone, sessionId)
      const qrDataURL = await this.generateQRCode(qrData, { width: 300 })
      
      // Create session with shorter expiration (1 hour)
      const sessionData = {
        customerId,
        customerPhone,
        sessionId,
        qrData,
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + (60 * 60 * 1000)), // 1 hour
        isUsed: false,
        usedAt: null,
        usedBy: null,
        branchId: null,
        isOneTime: true
      }

      await addDoc(collection(db, 'qrSessions'), sessionData)
      
      return {
        qrData,
        qrDataURL,
        sessionId
      }
    } catch (error) {
      console.error('Error generating one-time QR:', error)
      throw error
    }
  },

  // Get QR code statistics for a customer
  async getCustomerQRStats(customerId) {
    try {
      // This would query the database for QR session statistics
      // Implementation depends on your database structure
      return {
        totalSessions: 0,
        activeSessions: 0,
        usedSessions: 0,
        lastUsed: null
      }
    } catch (error) {
      console.error('Error getting QR stats:', error)
      throw error
    }
  }
} 